import { useId, type HTMLAttributes, type ReactNode } from 'react'
import { Meter, type MeterTone } from '../controls/Meter'
import { cx } from '../controls/cx'
import s from './CardProgress.module.css'

export interface CardProgressRow {
  key: string
  label: ReactNode
  /** How much is used, or `null` when no source answered — never a fabricated 0. */
  value: number | null
  max: number
  /** The figure as the host words it ("2.1 / 5 TB"). Defaults to "value / max". */
  display?: string
  tone?: MeterTone
  /** Why there is no value. Shown in place of the channel. */
  unavailable?: ReactNode
}

export interface CardProgressProps extends Omit<HTMLAttributes<HTMLUListElement>, 'children'> {
  rows: readonly CardProgressRow[]
  /** Names the list for a screen reader, when the card's heading does not. */
  label?: string
}

/** What a row with no value shows where its figure would be. */
const ABSENT = '—'

/**
 * THE PROGRESS ROWS (#2406, "Dashboards and Cards", the progress variant): a
 * label and its "34 / 40" figure, over a channel CUT into the slab — the kit's
 * `Meter`, whose track is the recess.
 *
 * Each channel is a real `meter`, named by its row's label and valued by its
 * figure, so the bar is not an image only sighted readers get. A row whose
 * value no source answered draws NO channel: an empty track reads as "none
 * used", which is a claim, and the reason is shown instead (absent is not
 * zero — the rule `StatStrip` holds for its figures).
 */
export function CardProgress({ rows, label, className, ...rest }: CardProgressProps) {
  const base = useId()
  if (rows.length === 0) return null
  return (
    <ul className={cx(s.list, className)} aria-label={label} data-card-progress="" {...rest}>
      {rows.map((row, index) => {
        const labelId = `${base}-${index}`
        const missing = row.value == null || !Number.isFinite(row.value)
        const figure = missing ? ABSENT : (row.display ?? `${row.value} / ${row.max}`)
        return (
          <li key={row.key} className={s.row}>
            <span className={s.label} id={labelId}>{row.label}</span>
            <span className={cx(s.figure, 'tnum')}>{figure}</span>
            {missing
              ? <span className={s.reason}>{row.unavailable ?? 'Not available'}</span>
              : (
                <div
                  className={s.channel}
                  role="meter"
                  aria-labelledby={labelId}
                  aria-valuemin={0}
                  aria-valuemax={row.max}
                  aria-valuenow={row.value as number}
                  aria-valuetext={row.display ?? `${row.value} of ${row.max}`}
                >
                  <Meter segments={[{ value: row.value as number, tone: row.tone ?? 'brand' }]} max={row.max} height={7} />
                </div>
              )}
          </li>
        )
      })}
    </ul>
  )
}
